import { type DuplicateAssetsBatchBody, ObjectAction, ObjectType } from '@/types/batch';
import { useBatchDuplicateAssets } from '@/hooks/data/assets/useBatchDuplicateAssets';
import { DuplicateAssetCommand } from '@/hooks/commands/asset/DuplicateAssetCommand';
import { useRestoreNodesMutation } from '@/hooks/tree/useRestoreNodesMutation';
import { useTrashNodesMutation } from '@/hooks/tree/useTrashNodesMutation';
import { useDuplicateAsset } from '@/hooks/data/assets/useDuplicateAsset';
import { useDuplicateStore } from '@/hooks/data/assets/useDuplicateStore';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useCommandContext } from '@/context/CommandContext';
import { type Asset } from '@/types/asset';
import { useTranslations } from 'next-intl';

/**
 * Shared by the right-click "Duplicate" menu item and the selection-bar
 * button, so both go through the same Command (and its undo/redo) instead
 * of duplicating the apply/update sequence in two places.
 */
export function useDuplicateAssetAction() {
  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const { apply } = useCommandContext();
  const { duplicateAssetMutation } = useDuplicateAsset();
  const { trashNodesMutation } = useTrashNodesMutation();
  const { restoreNodesMutation } = useRestoreNodesMutation();
  const addPendingDuplicate = useDuplicateStore((state) => state.addPendingDuplicate);
  const { batchDuplicateAssetsMutation } = useBatchDuplicateAssets();
  const openModal = useOpenModal();

  const duplicateAsset = async (assetId: string, destinationFolderId: string, name: string) => {
    const command = DuplicateAssetCommand(
      assetId,
      destinationFolderId,
      name,
      duplicateAssetMutation.mutateAsync,
      trashNodesMutation,
      restoreNodesMutation,
    );

    const response = await apply(command);
    command.update?.({ id: response.id });
    addPendingDuplicate(response.id);
  };

  // Swallowed: already surfaced via useBatchDuplicateAssets' onError, caught only to avoid
  // an unhandled rejection.
  const runBatchDuplicate = async (filter: DuplicateAssetsBatchBody['filter'], destinationFolderId: string) => {
    try {
      await batchDuplicateAssetsMutation.mutateAsync({
        objectType: ObjectType.Asset,
        objectAction: ObjectAction.Duplicate,
        filter,
        payload: { destinationFolderId },
      });
    } catch {
      // no-op, see above
    }
  };

  // Not routed through the Command stack - a batch duplicate isn't undoable.
  const confirmDuplicateAssets = (assets: Array<Asset>, destinationFolderId: string) => {
    openModal(ModalId.Confirm, {
      content: tDialogs('confirmDuplicateAssets', { count: assets.length }),
      confirmButtonVariant: 'success',
      confirmButtonLabel: tCommon('duplicate'),
      confirmButtonDataCy: 'duplicate-assets-confirm',
      dismissButtonDataCy: 'duplicate-assets-cancel',
      onConfirm: () => runBatchDuplicate({ assetIds: assets.map((asset) => asset.id) }, destinationFolderId),
    });
  };

  /**
   * "Select all" resolves every asset in the folder server-side rather than sending explicit
   * ids, since selectedAssets under select-all is only ever whatever page is currently loaded.
   */
  const confirmDuplicateAllAssets = (count: number, destinationFolderId: string, includeSubFolderAssets: boolean) => {
    openModal(ModalId.Confirm, {
      content: tDialogs('confirmDuplicateAllAssets', { count }),
      confirmButtonVariant: 'success',
      confirmButtonLabel: tCommon('duplicate'),
      confirmButtonDataCy: 'duplicate-assets-confirm',
      dismissButtonDataCy: 'duplicate-assets-cancel',
      onConfirm: () =>
        runBatchDuplicate({ folderId: destinationFolderId, includeSubFolderAssets }, destinationFolderId),
    });
  };

  return { duplicateAsset, confirmDuplicateAssets, confirmDuplicateAllAssets };
}
